/* eslint-disable */
/*
 *
 * GroupedRadioSelector
 * Generic component to select a grouped radio option with search functionality.
 *
 * @version 1.0.0
 * @author Patrizia Petrera
 * @since 04-Jul-2025
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import PortalSearchBar from '@nexidigital/portalsearchbar';
import GroupedRadio from 'Components/GroupedRadio';
import { ChevronDown } from '@nexidigital/iconlibrary';
import './GroupedRadioSelector.scss';

const GroupedRadioSelector = ({
  groupedOptions,
  optionsGroupedLabels,
  renderIcon,
  selectedOption,
  onChangeSelectedOption,
  selectPlaceholder,
  selectLabel,
  selectAllLabel,
  noResultsLabel,
  searchPlaceholder,
}) => {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const selectorRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(event) {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectorRef]);

  const flattedOptions = useMemo(
    () =>
      Object.values(groupedOptions)
        .flat()
        .filter(
          (option) =>
            (option.label && option.label.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (option.id && option.id.includes(searchQuery))
        ),
    [groupedOptions, searchQuery]
  );

  const isAllSelected = !!selectAllLabel && selectedOption.length === 0;

  const radioOptions = useMemo(() => [
    ...(selectAllLabel ? [{
      id: 'ALL',
      value: 'ALL',
      label: (
        <div className={`groupedRadioInfo ${isAllSelected ? 'selected' : ''}`}>
          <h1 className='groupedRadioLabel'>{selectAllLabel}</h1>
        </div>
      ),
      checked: isAllSelected,
    }] : []),
    ...flattedOptions.map((option) => ({
      id: option.id,
      value: option.id,
      label: (
        <div className={`groupedRadioInfo ${selectedOption.includes(option.id) ? 'selected' : ''}`}>
          {renderIcon ? renderIcon(option) : null}
          <h1 className='groupedRadioLabel'>{option.label}</h1>
        </div>
      ),
      checked: selectedOption.includes(option.id),
    }))
  ], [flattedOptions, selectAllLabel, isAllSelected, selectedOption, renderIcon]);

  const resolvedOptionsGrouped = useMemo(() =>
    selectAllLabel ? { _all: [{ id: 'ALL' }], ...groupedOptions } : groupedOptions
  , [selectAllLabel, groupedOptions]);

  const resolvedOptionsGroupedLabels = useMemo(() =>
    selectAllLabel ? { _all: '', ...optionsGroupedLabels } : optionsGroupedLabels
  , [selectAllLabel, optionsGroupedLabels]);

  return (
    <div className='groupedSelector' ref={selectorRef}>
      <span className='transactionSectionHeading'>{selectLabel}</span>
      <div className='dropdownSelector'>
        <div
          role='button'
          tabIndex={0}
          onClick={() => setDropdownOpen(!isDropdownOpen)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setDropdownOpen(!isDropdownOpen);
            }
          }}
          aria-expanded={isDropdownOpen}
          aria-label='Toggle dropdown'
          className={`dropdownInput ${isDropdownOpen ? 'openDrop' : ''}`}>
          <span className={`dropdownPlaceholder ${!isAllSelected && selectedOption.length === 0 ? 'placeholder' : ''}`}>
            {isAllSelected
              ? selectAllLabel
              : selectedOption.length === 0
                ? selectPlaceholder
                : selectedOption.length === 1
                  ? Object.values(groupedOptions)
                    .flat()
                    .find((opt) => opt.id === selectedOption[0])?.label || selectedOption[0]
                  : null}
          </span>
          <div className={`chevronWrapper ${isDropdownOpen ? 'open' : ''}`}>
            <ChevronDown size='24' type='line' />
          </div>
        </div>
      </div>

      {isDropdownOpen && (
        <div className='dropdownPanel'>
          <div className='searchWrapper'>
            <PortalSearchBar
              id='store-filter'
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(value) => setSearchQuery(value)}
              handleIconClick={() => setSearchQuery('')}
              disableSendAnalytics
              cleanField
              liveSearch
              disableSearchIcon
            />
          </div>

          <div className='checkboxWrapper'>
            <div className='checkboxHeader'>
              <GroupedRadio
                options={radioOptions}
                optionsGrouped={resolvedOptionsGrouped}
                optionsGroupedLabels={resolvedOptionsGroupedLabels}
                defaultValue={isAllSelected ? 'ALL' : selectedOption[0] || ''}
                onChange={(value) => {
                  if (value === 'ALL') {
                    onChangeSelectedOption([]);
                  } else {
                    onChangeSelectedOption([value]);
                  }
                  setDropdownOpen(false);
                }}
              />
              {flattedOptions.length === 0 && searchQuery && <div className='noResults'>{noResultsLabel}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

GroupedRadioSelector.propTypes = {
  selectPlaceholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  selectLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  selectAllLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  noResultsLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  searchPlaceholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  groupedOptions: PropTypes.object,
  optionsGroupedLabels: PropTypes.object,
  selectedOption: PropTypes.array,
  onChangeSelectedOption: PropTypes.func,
  renderIcon: PropTypes.func,
};

GroupedRadioSelector.defaultProps = {
  selectPlaceholder: '',
  selectLabel: '',
  selectAllLabel: '',
  noResultsLabel: '',
  searchPlaceholder: '',
  groupedOptions: {},
  optionsGroupedLabels: {},
  selectedOption: [],
  onChangeSelectedOption: () => { },
  renderIcon: () => { },
};

export default GroupedRadioSelector;
